import { router } from "@inertiajs/react"
import { MoreHorizontal, Plus } from "lucide-react"

import { EditableCell } from "@/components/bids/EditableCell"
import { GmChip } from "@/components/bids/GmChip"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { money, pct } from "@/lib/money"
import { pluralize } from "@/lib/relative-time"
import type { WorkspaceLevel } from "@/types/bids"

/** The design's level summary cards: one QUOTED PRICE per level (inline
 *  editable — every product inherits it) + target GM + the level's worst
 *  product GM as the health chip. */
export function LevelSummaryCards({
  levels,
  onAddLevel,
  onEditLevel,
}: {
  levels: WorkspaceLevel[]
  onAddLevel: () => void
  onEditLevel: (level: WorkspaceLevel) => void
}) {
  const patchLevel = (level: WorkspaceLevel, attrs: Record<string, string>) =>
    router.patch(level.urls.update, { bid_level: attrs }, { preserveScroll: true })

  return (
    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
      {levels.map((level) => (
        <div
          key={level.id}
          className="rounded-lg border border-hairline bg-surface p-4"
          data-testid={`level-card-${level.id}`}
        >
          <p className="flex items-center justify-between gap-2 text-sm text-ink-muted">
            <span>
              {level.name} · {pluralize(level.product_count, "product")}
            </span>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  aria-label={`${level.name} actions`}
                  data-testid={`level-menu-${level.id}`}
                >
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={() => onEditLevel(level)}>
                  Edit level…
                </DropdownMenuItem>
                <DropdownMenuItem
                  destructive
                  onSelect={() => router.delete(level.urls.update, { preserveScroll: true })}
                >
                  Remove level
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </p>

          <p className="mt-2 text-lg">
            <EditableCell
              value={level.quoted_price}
              display={money(level.quoted_price)}
              suffix={`/${level.unit_label}`}
              onCommit={(raw) => patchLevel(level, { quoted_price: raw })}
              testid={`quoted-cell-${level.id}`}
              className="font-semibold text-ink-display"
            />
          </p>

          <p className="mt-1.5 flex items-center gap-2 text-sm">
            {/* The chip edits in BOTH directions: it shows the level's
                worst-product GM, and typing a margin has the server compute
                the quoted price that meets it (price follows margin, the
                reverse of the quoted cell). Uncosted levels can't. */}
            <EditableCell
              value={level.summary.worst_gm ?? ""}
              display={<GmChip actual={level.summary.worst_gm} target={level.target_gm_pct} />}
              onCommit={(raw) =>
                router.post(level.urls.apply_margin, { gm_pct: raw }, { preserveScroll: true })
              }
              disabled={level.summary.worst_gm == null}
              testid={`gm-chip-level-${level.id}`}
              className="px-0"
            />
            <span className="text-ink-muted">
              target{" "}
              <EditableCell
                value={level.target_gm_pct}
                display={pct(level.target_gm_pct)}
                onCommit={(raw) => patchLevel(level, { target_gm_pct: raw })}
                testid={`target-gm-cell-${level.id}`}
              />
            </span>
          </p>

          {level.summary.uncosted_count > 0 && (
            <p className="mt-1.5">
              <Badge tone="signal">{level.summary.uncosted_count} uncosted</Badge>
            </p>
          )}
        </div>
      ))}

      <button
        type="button"
        onClick={onAddLevel}
        className="flex min-h-24 items-center justify-center gap-1.5 rounded-lg border border-dashed border-hairline text-sm text-ink-muted hover:border-accent hover:text-ink-body"
        data-testid="add-level"
      >
        <Plus className="h-4 w-4" /> Add level
      </button>
    </div>
  )
}
